import React, { useEffect, useState } from 'react';
import { engageKillSwitch, releaseKillSwitch, getKillSwitch } from '../../services/api';
import { PAGE_TITLE } from './typeScale';

const unwrap = (res) => (res && res.data !== undefined ? res.data : res);

/**
 * What this agent is allowed to be, and the one control that stops it.
 *
 * <h3>Widening is not a quiet edit</h3>
 *
 * Each of these four was attested to by the accountable owner. Widening any of
 * them re-hashes the charter and asks them to attest again, and the surface
 * says so where the Edit buttons are rather than in a dialog afterwards.
 *
 * <h3>Pausing is real, reversible, and says so</h3>
 *
 * The kill switch is a runtime halt enforced at the gateway: engaging stops all
 * governed execution at once, releasing resumes it, and nothing is deleted
 * either way. A control that reads as destructive gets used late or not at all,
 * and this one wants to be reached for early.
 */
export default function ProtectChange({
  agentId, intent = [], rules = [], runners = [], owner, onEdit, onBack,
}) {
  const [paused, setPaused] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!agentId) return undefined;
    let alive = true;
    (async () => {
      try {
        const s = unwrap(await getKillSwitch(agentId));
        if (alive) setPaused(Boolean(s?.engaged));
      } catch { /* leave it as running; the button still works */ }
    })();
    return () => { alive = false; };
  }, [agentId]);

  const toggle = async () => {
    setBusy(true); setError(null);
    try {
      if (paused) { await releaseKillSwitch(agentId); setPaused(false); } else {
        await engageKillSwitch(agentId, 'Paused from Protect');
        setPaused(true);
      }
    } catch (e) {
      setError(e?.message || 'Could not reach the gateway just now.');
    } finally {
      setBusy(false);
    }
  };

  const cards = [
    ['intent', 'What it may do', intent, 'Edit'],
    ['rules', 'What must never leave', rules, 'Edit'],
    ['runners', 'Who may run it', runners, 'Edit'],
    ['owner', 'Who answers for it', owner ? [owner] : [], 'Re-assign'],
  ];

  return (
    <div style={{ minWidth: 0, width: '100%', maxWidth: '760px', margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--p7-space-4)',
                    marginBottom: 'var(--p7-space-4)' }}>
        <h1 style={{ ...PAGE_TITLE, flex: 1 }}>Change</h1>
        {onBack ? (
          <button type="button" className="p7-btn p7-btn--sm" onClick={onBack}>Back to agent</button>
        ) : null}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {cards.map(([key, title, values, action]) => (
          <div key={key} data-testid={`card-${key}`} className="p7-card"
               style={{ padding: 'var(--p7-space-4)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--p7-space-3)',
                          marginBottom: 'var(--p7-space-3)' }}>
              <h3 style={{ margin: 0, flex: 1, font: 'var(--p7-text-ui)',
                           fontFamily: 'var(--p7-font-sans)', fontWeight: 600 }}>{title}</h3>
              <button type="button" className="p7-btn p7-btn--sm"
                      onClick={() => onEdit && onEdit(key)}>{action}</button>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {values.length ? values.map((v) => (
                <span key={v} className="p7-btn p7-btn--sm"
                      style={{ borderColor: 'var(--p7-accent)', color: 'var(--p7-accent)',
                               background: 'var(--p7-accent-bg)', cursor: 'default' }}>{v}</span>
              )) : (
                <span style={{ font: 'var(--p7-text-caption)',
                               fontFamily: 'var(--p7-font-sans)',
                               color: 'var(--p7-ink-subtle)' }}>Nothing recorded.</span>
              )}
            </div>
          </div>
        ))}
      </div>

      <p style={{ margin: 'var(--p7-space-3) 0 0', font: 'var(--p7-text-caption)',
                  fontFamily: 'var(--p7-font-sans)', color: 'var(--p7-ink-subtle)' }}>
        Widening any of these re-hashes the charter and asks the owner to attest again.
      </p>

      {error ? (
        <div className="p7-note p7-note--deny" style={{ marginTop: 'var(--p7-space-3)' }}>
          {error}
        </div>
      ) : null}

      <div className="p7-card"
           style={{ marginTop: 'var(--p7-space-5)', padding: 'var(--p7-space-4)',
                    display: 'flex', alignItems: 'center', gap: 'var(--p7-space-4)',
                    borderLeft: '3px solid var(--p7-deny)' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ margin: 0, font: 'var(--p7-text-ui)', fontFamily: 'var(--p7-font-sans)',
                       fontWeight: 600 }}>{paused ? 'It is paused' : 'Pause it'}</h3>
          <p style={{ margin: '1px 0 0', font: 'var(--p7-text-caption)',
                      fontFamily: 'var(--p7-font-sans)', color: 'var(--p7-ink-subtle)' }}>
            {paused
              ? 'Every call is being refused at the gateway. Resuming puts it back exactly as it was.'
              : 'Stops every call at once. Nothing is deleted.'}
          </p>
        </div>
        <button type="button" className="p7-btn" disabled={busy} onClick={toggle}
                style={paused ? undefined : { color: 'var(--p7-deny)' }}>
          {busy ? 'Working…' : paused ? 'Resume' : 'Pause'}
        </button>
      </div>
    </div>
  );
}
